import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { IdleReasonDialog } from "@/components/IdleReasonDialog";
import { Bell, Check, CheckCheck, Package, Send, Printer, Upload , Scale } from "lucide-react";

const KIND_ICON = {
  upload: <Upload className="w-3.5 h-3.5 text-blue-600" />,
  share: <Send className="w-3.5 h-3.5 text-teal-600" />,
  printed: <Printer className="w-3.5 h-3.5 text-slate-600" />,
  info: <Package className="w-3.5 h-3.5 text-indigo-600" />,
  weight_reminder: <Scale className="w-3.5 h-3.5 text-amber-600" />,
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 60 * 24) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [idleRef, setIdleRef] = useState(null);
  const lastUnreadRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications?limit=30`, { headers: getAuthHeader() });
      setItems(r.data.notifications || []);
      const newUnread = r.data.unread || 0;
      // Fire a toast for newly-arrived notifications (compared to last poll)
      if (newUnread > lastUnreadRef.current && lastUnreadRef.current > 0) {
        const latest = (r.data.notifications || []).find(n => !n.read);
        if (latest) toast(latest.title, { description: latest.body });
      }
      lastUnreadRef.current = newUnread;
      setUnread(newUnread);
    } catch {
      /* silently ignore — polling should not spam errors */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 20000);   // 20s polling, visible tabs only
    return () => clearInterval(id);
  }, [load]);

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/notifications/read`, { id }, { headers: getAuthHeader() });
      await load();
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
      await load();
    } catch { /* ignore */ }
  };

  return (
    <>
    <IdleReasonDialog idleRef={idleRef} onClose={() => setIdleRef(null)} />
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 min-w-4 p-0.5 text-[10px] bg-red-500 text-white border-white border-2 rounded-full flex items-center justify-center"
                   data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notif-dropdown">
        <div className="flex items-center justify-between p-3 border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{unread} unread</Badge>}
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                    onClick={markAll} data-testid="notif-mark-all">
              <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500" data-testid="notif-empty">
              No notifications yet
            </div>
          ) : items.map(n => (
            <div key={n.id}
                 className={`flex items-start gap-2 p-3 border-b text-xs hover:bg-slate-50 cursor-pointer ${
                   n.read ? "" : "bg-blue-50/40"
                 }`}
                 onClick={() => {
                   if (!n.read) markOne(n.id);
                   // Iter 279 — in-app deep links (e.g. Ageing digest)
                   if (n.link && n.link.startsWith("/?")) {
                     window.history.pushState({}, "", n.link);
                     window.dispatchEvent(new Event("panelchange"));
                     window.dispatchEvent(new Event("or-deeplink"));
                   }
                 }}
                 data-testid={`notif-item-${n.id}`}>
              <div className="mt-0.5">{KIND_ICON[n.kind] || KIND_ICON.info}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{n.title}</div>
                <div className={`text-slate-500 ${n.digest_run_id ? "whitespace-normal" : "truncate"}`}>{n.body}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{fmtWhen(n.created_at)}</div>
                {n.idle_ref && (
                  <button type="button" data-testid={`notif-idle-reason-${n.id}`}
                    onClick={(e) => { e.stopPropagation(); if (!n.read) markOne(n.id); setOpen(false); setIdleRef(n.idle_ref); }}
                    className="mt-1 inline-flex items-center rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-0.5 text-[11px] font-semibold">
                    Give idle reason
                  </button>
                )}
              </div>
              {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>}
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
};

export default NotificationBell;
